/**
 * Service for managing timeframe options in Firestore
 * Provides CRUD operations and caching for timeframe configuration
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase.js';

class TimeframesService {
  constructor() {
    this.collectionName = 'timeframes';
    this.cache = null;
    this.cacheTimestamp = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get collection reference
   */
  getCollectionRef() {
    return collection(db, this.collectionName);
  }

  /**
   * Check if cache is valid
   */
  isCacheValid() {
    if (!this.cache || !this.cacheTimestamp) return false;
    return Date.now() - this.cacheTimestamp < this.cacheDuration;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
  }

  /**
   * Get all timeframes (with caching)
   * @returns {Promise<Array>} Array of timeframe objects
   */
  async getAll() {
    // Return cached data if valid
    if (this.isCacheValid()) {
      return this.cache;
    }

    try {
      const q = query(this.getCollectionRef(), orderBy('days', 'asc'));
      const snapshot = await getDocs(q);

      const timeframes = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(tf => tf.active !== false); // Only return active timeframes

      // Update cache
      this.cache = timeframes;
      this.cacheTimestamp = Date.now();

      return timeframes;
    } catch (error) {
      console.error('Error fetching timeframes:', error);
      throw error;
    }
  }

  /**
   * Get all timeframes including inactive ones
   * @returns {Promise<Array>} Array of all timeframe objects
   */
  async getAllIncludingInactive() {
    try {
      const q = query(this.getCollectionRef(), orderBy('days', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching all timeframes:', error);
      throw error;
    }
  }

  /**
   * Get single timeframe by ID
   * @param {string} id - Document ID
   * @returns {Promise<Object|null>} Timeframe object or null
   */
  async getById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching timeframe:', error);
      throw error;
    }
  }

  /**
   * Get timeframe by value field
   * @param {string} value - The value field to search for
   * @returns {Promise<Object|null>} Timeframe object or null
   */
  async getByValue(value) {
    const timeframes = await this.getAll();
    return timeframes.find(tf => tf.value === value) || null;
  }

  /**
   * Create a new timeframe
   * @param {Object} data - Timeframe data
   * @returns {Promise<string>} New document ID
   */
  async create(data) {
    try {
      const docData = {
        ...data,
        active: data.active !== undefined ? data.active : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getCollectionRef(), docData);
      this.clearCache();
      return docRef.id;
    } catch (error) {
      console.error('Error creating timeframe:', error);
      throw error;
    }
  }

  /**
   * Update an existing timeframe
   * @param {string} id - Document ID
   * @param {Object} data - Updated data
   * @returns {Promise<void>}
   */
  async update(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      this.clearCache();
    } catch (error) {
      console.error('Error updating timeframe:', error);
      throw error;
    }
  }

  /**
   * Delete a timeframe
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      this.clearCache();
    } catch (error) {
      console.error('Error deleting timeframe:', error);
      throw error;
    }
  }

  /**
   * Soft delete - mark as inactive
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async deactivate(id) {
    await this.update(id, { active: false });
  }

  /**
   * Reactivate a timeframe
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async activate(id) {
    await this.update(id, { active: true });
  }

  /**
   * Seed initial timeframes from hardcoded data
   * @param {Array} timeframes - Array of timeframe objects
   * @returns {Promise<void>}
   */
  async seedInitialData(timeframes) {
    try {
      const batch = writeBatch(db);

      timeframes.forEach((tfData) => {
        const docRef = doc(this.getCollectionRef());
        batch.set(docRef, {
          ...tfData,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      this.clearCache();
      console.log(`Successfully seeded ${timeframes.length} timeframes`);
    } catch (error) {
      console.error('Error seeding timeframes:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const timeframesService = new TimeframesService();
export default timeframesService;
